import { type ShiftWithAdjustments } from "@src/appV2/Shifts/types";
import { getHours, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";

import {
  type GroupedShiftCards,
  type GroupedShiftCounts,
  type GroupedShifts,
  type TimeSlot,
  type TimeSlotCounts,
} from "../types";
import { type ShiftCardData } from "./mapShiftToCard";

/**
 * Determines the time slot (am/pm/noc) based on the hour of the day.
 *
 * - AM: 5:00 - 11:59
 * - PM: 12:00 - 17:59
 * - NOC: 18:00 - 4:59
 */
export function getTimeSlotFromHour(hour: number): TimeSlot {
  if (hour >= 5 && hour < 12) {
    return "am";
  }

  if (hour >= 12 && hour < 18) {
    return "pm";
  }

  return "noc";
}

/**
 * Determines the time slot from a date string.
 * @param dateString - ISO date string
 * @param timezone - Optional timezone to convert the date to (e.g., "America/Los_Angeles")
 */
export function getTimeSlotFromDateString(dateString: string, timezone?: string): TimeSlot {
  const date = parseISO(dateString);
  const zonedDate = timezone ? utcToZonedTime(date, timezone) : date;
  const hour = getHours(zonedDate);
  return getTimeSlotFromHour(hour);
}

/**
 * Groups shifts by their time slot based on the shift start time.
 */
export function groupShiftsByTimeSlot(shifts: ShiftWithAdjustments[]): GroupedShifts {
  const grouped: GroupedShifts = {
    am: [],
    pm: [],
    noc: [],
  };

  for (const shift of shifts) {
    const timeSlot = getTimeSlotFromDateString(shift.start);
    grouped[timeSlot].push(shift);
  }

  return grouped;
}

/**
 * Calculates the counts for a list of shifts.
 *
 * - filledShiftsCount: shifts with an assigned worker (agent.userId exists)
 * - unfilledShiftsCount: shifts without an assigned worker
 */
export function calculateTimeSlotCounts(shifts: ShiftWithAdjustments[]): TimeSlotCounts {
  let filledShiftsCount = 0;
  let unfilledShiftsCount = 0;

  for (const shift of shifts) {
    const hasFilled = Boolean(shift.agent?.userId);

    if (hasFilled) {
      filledShiftsCount += 1;
    } else {
      unfilledShiftsCount += 1;
    }
  }

  return {
    filledShiftsCount,
    unfilledShiftsCount,
    warningShiftsCount: 0,
  };
}

/**
 * Calculates counts for all time slots from grouped shifts.
 */
export function calculateGroupedCounts(groupedShifts: GroupedShifts): GroupedShiftCounts {
  return {
    am: calculateTimeSlotCounts(groupedShifts.am),
    pm: calculateTimeSlotCounts(groupedShifts.pm),
    noc: calculateTimeSlotCounts(groupedShifts.noc),
  };
}

/**
 * Groups shift cards by their time slot based on the shift start time.
 */
export function groupShiftCardsByTimeSlot(shifts: ShiftCardData[]): GroupedShiftCards {
  const grouped: GroupedShiftCards = {
    am: [],
    pm: [],
    noc: [],
  };

  for (const shift of shifts) {
    const timeSlot = getTimeSlotFromDateString(shift.startTime);
    grouped[timeSlot].push(shift);
  }

  return grouped;
}

/**
 * Calculates the counts for a list of shift cards.
 *
 * - filledShiftsCount: shifts with a worker assigned (workerId exists)
 * - unfilledShiftsCount: shifts without a worker assigned
 */
export function calculateShiftCardCounts(shifts: ShiftCardData[]): TimeSlotCounts {
  let filledShiftsCount = 0;
  let unfilledShiftsCount = 0;

  for (const shift of shifts) {
    const isFilled = Boolean(shift.workerId);

    if (isFilled) {
      filledShiftsCount += 1;
    } else {
      unfilledShiftsCount += 1;
    }
  }

  return {
    filledShiftsCount,
    unfilledShiftsCount,
    warningShiftsCount: 0,
  };
}

/**
 * Calculates counts for all time slots from grouped shift cards.
 */
export function calculateGroupedShiftCardCounts(
  groupedShifts: GroupedShiftCards
): GroupedShiftCounts {
  return {
    am: calculateShiftCardCounts(groupedShifts.am),
    pm: calculateShiftCardCounts(groupedShifts.pm),
    noc: calculateShiftCardCounts(groupedShifts.noc),
  };
}
